import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useViewer } from '@/features/session';
import { planName } from '@/shared/config/plans';
import { ROUTES } from '@/shared/config/routes';
import { track } from '@/shared/lib/analytics';
import { formatMonthDay } from '@/shared/lib/format';
import { notify } from '@/shared/lib/toast';
import { isUpgrade } from '../model/checkout-summary';
import type { CheckoutPhase, PaidPlanId, WorkspaceData } from '../types';
import { useChangePlan } from './use-change-plan';
import { usePaddleCheckout } from './use-paddle-checkout';
import { usePlanActivation } from './use-plan-activation';
import { useStartCheckout } from './use-start-checkout';

export type CheckoutView = 'form' | 'activating' | 'success' | 'timeout';

export type UseCheckoutFlowResult = {
  view: CheckoutView;
  plan: PaidPlanId;
  currentPlan: PaidPlanId | null;
  isChange: boolean;
  isPaymentsOff: boolean;
  phase: CheckoutPhase;
  isChanging: boolean;
  pickPlan: (plan: PaidPlanId) => void;
  subscribe: () => Promise<void>;
  confirmChange: () => Promise<void>;
};

const asPaidPlan = (value: string | null | undefined): PaidPlanId | null =>
  value === 'PRO' || value === 'TEAM' ? value : null;

/** Pick a plan, pay in Paddle's frame, wait for the webhook, show the plan (design: Checkout). */
export function useCheckoutFlow(workspace: WorkspaceData): UseCheckoutFlowResult {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshViewer } = useViewer();
  const subscription = workspace.billing.subscription;
  const currentPlan = asPaidPlan(subscription?.plan);
  const isChange = currentPlan !== null && subscription?.status !== 'CANCELED';
  const [plan, setPlan] = useState<PaidPlanId>(
    () =>
      asPaidPlan(searchParams.get('plan')?.toUpperCase()) ??
      (currentPlan === 'PRO' ? 'TEAM' : currentPlan === 'TEAM' ? 'PRO' : 'PRO'),
  );
  const [view, setView] = useState<CheckoutView>('form');
  const [activationPlan, setActivationPlan] = useState<PaidPlanId | null>(null);
  const [isPaymentsOff, setIsPaymentsOff] = useState(workspace.billing.paddle === null);
  const checkout = usePaddleCheckout(workspace.billing.paddle);
  const activation = usePlanActivation(activationPlan);
  const { startCheckout } = useStartCheckout();
  const { changePlan, isChanging } = useChangePlan();

  useEffect(() => {
    if (checkout.phase === 'completed' && view === 'form') {
      setActivationPlan(plan);
      setView('activating');
    }
  }, [checkout.phase, plan, view]);

  useEffect(() => {
    if (activation === 'active') {
      track('plan_activated', { plan: activationPlan?.toLowerCase() });
      setView('success');
    }
    if (activation === 'timeout') setView('timeout');
  }, [activation, activationPlan]);

  const pickPlan = (next: PaidPlanId) => {
    if (next === currentPlan && isChange) return;
    if (checkout.phase !== 'idle') checkout.close();
    setPlan(next);
  };

  const subscribe = async () => {
    checkout.setStarting();
    const outcome = await startCheckout(plan);
    if (outcome.transactionId) {
      track('begin_checkout', { plan: plan.toLowerCase() });
      await checkout.openInline(outcome.transactionId);
      return;
    }
    checkout.reset();
    if (outcome.errorCode === 'PAYMENTS_OFF') setIsPaymentsOff(true);
    if (outcome.errorCode === 'ALREADY_SUBSCRIBED') await refreshViewer();
  };

  const confirmChange = async () => {
    if (!currentPlan) return;
    const outcome = await changePlan(plan);
    if (!outcome.subscription) {
      if (outcome.errorCode === 'PAYMENTS_OFF') setIsPaymentsOff(true);
      return;
    }
    if (isUpgrade(currentPlan, plan)) {
      setActivationPlan(plan);
      setView('activating');
      return;
    }
    const takesOver = outcome.subscription.currentPeriodEndsAt;
    notify.success(
      takesOver
        ? `${planName(plan)} takes over on ${formatMonthDay(takesOver)}.`
        : `${planName(plan)} takes over at the end of the period.`,
    );
    await refreshViewer();
    navigate(ROUTES.BILLING);
  };

  return {
    view,
    plan,
    currentPlan,
    isChange,
    isPaymentsOff,
    phase: checkout.phase,
    isChanging,
    pickPlan,
    subscribe,
    confirmChange,
  };
}
